<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    @mousedown.self="close"
  >
    <div class="flex flex-col bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4" style="max-height: 90vh">
      <div class="flex items-start justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h3 class="text-lg font-bold text-gray-800">Double kayıtları dağıt</h3>
          <p class="text-sm text-gray-500">
            Excel #{{ report.excel_id }}
            <template v-if="breakdown">
              · <strong class="text-gray-800">{{ breakdown.total }}</strong> kayıt
              <span v-if="breakdown.missing_count">
                ({{ breakdown.missing_count }} kayıt sistemde bulunamadı)</span>
            </template>
          </p>
        </div>
        <button
          type="button"
          class="text-2xl leading-none text-gray-400 hover:text-gray-600"
          :disabled="running"
          @click="close"
        >
          &times;
        </button>
      </div>

      <div v-if="loading" class="px-6 py-10 text-center text-gray-400">Yükleniyor...</div>

      <div v-else-if="!breakdown" class="px-6 py-10 text-center text-red-600">
        {{ error || "Veri yüklenemedi." }}
      </div>

      <div v-else class="grid gap-6 px-6 py-5 overflow-y-auto md:grid-cols-5">
        <!-- Kırılım: dağıtılacak kayıtları seç -->
        <div class="space-y-3 md:col-span-3">
          <div class="flex flex-wrap items-center gap-2 text-sm">
            <span>
              Seçili: <strong class="text-gray-900">{{ selectedCount }}</strong> /
              {{ breakdown.total }} kayıt
            </span>
            <span
              v-for="dim in activeFilterDims"
              :key="dim.key"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 ring-1 ring-blue-200"
            >
              {{ dim.label }}: {{ selectedLabels(dim.key) }}
              <button type="button" class="leading-none" :disabled="running" @click="selected[dim.key] = []">&times;</button>
            </span>
            <button
              v-if="activeFilterDims.length"
              type="button"
              class="text-xs text-gray-500 underline"
              :disabled="running"
              @click="clearFilters"
            >
              Filtreyi temizle
            </button>
          </div>

          <div class="tabs">
            <button
              v-for="dim in dimensions"
              :key="dim.key"
              type="button"
              :class="{ active: tab == dim.key }"
              @click="setTab(dim.key)"
            >
              {{ dim.label }}
              <span v-if="selected[dim.key].length" class="ml-1 text-xs">({{ selected[dim.key].length }})</span>
            </button>
          </div>

          <input
            v-if="facetRows.length > 8 || search"
            v-model="search"
            type="text"
            class="uk-input"
            placeholder="Ara..."
          />

          <div class="rounded-lg ring-1 ring-gray-200 text-sm">
            <label
              v-if="visibleFacetRows.length"
              class="flex items-center justify-between px-3 py-2 border-b border-gray-100 cursor-pointer bg-gray-50 rounded-t-lg"
            >
              <span class="flex items-center space-x-2 font-medium">
                <input
                  type="checkbox"
                  class="uk-checkbox"
                  :checked="allVisibleSelected"
                  :disabled="running"
                  @change="toggleAllVisible"
                />
                <span>{{ currentDim.label }}</span>
              </span>
              <span class="text-xs text-gray-500">Kayıt</span>
            </label>
            <div class="overflow-y-auto max-h-72">
              <div v-if="!visibleFacetRows.length" class="px-3 py-4 text-center text-gray-400">
                Kayıt yok
              </div>
              <label
                v-for="row in visibleFacetRows"
                :key="row.value"
                class="flex items-center gap-3 px-3 py-1.5 cursor-pointer hover:bg-gray-50"
                :class="{ 'opacity-50': !row.count }"
              >
                <input
                  type="checkbox"
                  class="uk-checkbox"
                  :checked="selected[tab].includes(row.value)"
                  :disabled="running"
                  @change="toggleValue(tab, row.value)"
                />
                <span class="w-40 truncate sm:w-56" :title="row.label">{{ row.label }}</span>
                <span class="flex-1 h-2 overflow-hidden bg-gray-100 rounded-full">
                  <span
                    class="block h-full bg-blue-500 rounded-full"
                    :style="{ width: (row.count / facetMax) * 100 + '%' }"
                  ></span>
                </span>
                <strong class="w-12 text-right">{{ row.count }}</strong>
              </label>
            </div>
          </div>
          <p class="text-xs text-gray-500">
            Seçim yapmazsanız tüm double kayıtlar dağıtılır. Farklı sekmelerdeki seçimler
            birlikte uygulanır (ör. UTM: google ve Agent: Ali → Ali'deki google kayıtları).
          </p>

          <div v-if="breakdown.history.length" class="text-sm rounded-lg ring-1 ring-gray-200">
            <button
              type="button"
              class="flex justify-between w-full px-3 py-2 font-medium text-left"
              @click="historyOpen = !historyOpen"
            >
              <span>Dağıtım geçmişi ({{ breakdown.history.length }})</span>
              <span>{{ historyOpen ? "−" : "+" }}</span>
            </button>
            <div v-if="historyOpen" class="overflow-y-auto border-t border-gray-100 divide-y divide-gray-100 max-h-60">
              <div v-for="item in breakdown.history" :key="item.id" class="px-3 py-2 space-y-0.5">
                <div class="flex justify-between">
                  <span>
                    <strong>{{ item.lead_count }}</strong> kayıt ·
                    {{ breakdown.users[item.user_id] || "bilinmeyen kullanıcı" }}
                  </span>
                  <span class="text-xs text-gray-500">{{ item.created_at }}</span>
                </div>
                <div class="text-xs text-gray-500">Kırılım: {{ describeFilters(item.filters) }}</div>
                <div class="text-xs text-gray-700">{{ describeSummary(item.summary) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Kime atanacak -->
        <div class="space-y-4 md:col-span-2">
          <h4 class="font-semibold text-gray-800">Kime atanacak</h4>

          <div v-for="slot in slots" :key="slot.field" class="space-y-2">
            <label class="flex items-center space-x-2 font-medium cursor-pointer">
              <input
                type="checkbox"
                class="uk-checkbox"
                v-model="slot.enabled"
                :disabled="running"
              />
              <span>{{ slot.label }} ataması</span>
            </label>
            <template v-if="slot.enabled">
              <div class="assign-mode">
                <button
                  type="button"
                  :class="{ active: !slot.all }"
                  :disabled="running"
                  @click="slot.all = false"
                >Seçilen kişilere</button>
                <button
                  type="button"
                  :class="{ active: slot.all }"
                  :disabled="running"
                  title="Listedeki tüm kişiler arasında eşit dağıtılır"
                  @click="slot.all = true"
                >Herkese eşit</button>
              </div>
              <bulk-distribute-picker
                v-if="!slot.all"
                v-model="slot.users"
                inline
                :options="options[slot.field]"
                :record-count="selectedCount"
              />
              <p v-else class="text-sm text-gray-500">
                {{ options[slot.field].length }} kişi · kişi başı
                {{ perPerson(options[slot.field].length) }} kayıt
              </p>
            </template>
          </div>

          <label
            v-if="slots[0].enabled"
            class="flex items-center space-x-2 text-sm cursor-pointer"
            title="Kayıtların mevcut notları satışçılara gizlenecektir"
          >
            <input type="checkbox" class="uk-checkbox" v-model="hideNotes" :disabled="running" />
            <span>Notları gizleyerek ata</span>
          </label>

          <div v-if="lastResult" class="p-3 text-sm rounded-lg bg-green-50 text-green-800 ring-1 ring-green-200">
            <strong>{{ lastResult.lead_count }}</strong> kayıt dağıtıldı
            ({{ describeFilters(lastResult.filters) }}).
            <div class="mt-1 text-xs">{{ describeSummary(lastResult.distribution) }}</div>
          </div>
          <p v-if="error" class="p-3 text-sm rounded-lg bg-red-50 text-red-700">{{ error }}</p>
        </div>
      </div>

      <div class="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-lg">
        <button type="button" class="global-button" :disabled="running" @click="close">Kapat</button>
        <button
          v-if="breakdown"
          type="button"
          class="primary-button"
          :disabled="running || !selectedCount"
          @click="submit"
        >
          {{ running ? "Dağıtılıyor..." : selectedCount + " kaydı dağıt" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import BulkDistributePicker from "./BulkDistributePicker.vue";
import EventBus from "../Ui/EventBus";
import http from "../../services/http";

// Kırılım boyutları; kullanıcı alanlarında 0 = atanmamış, utm'de "" = boş
const DIMENSIONS = [
  { key: "agent_id", label: "Agent", user: true },
  { key: "retention_id", label: "Retention", user: true },
  { key: "utm_source", label: "UTM Source", user: false },
];

// Import raporundaki double kayıtları (eşleşen mevcut lead'ler) agent / retention /
// utm_source kırılımında gösterir; seçilen kırılımdaki kayıtları agent / retention
// alanlarında seçilen kişilere eşit dağıtır. Kayıtları backend seçer ve tek
// transaction'da yazar; ekrandaki durum eskiyse (başka dağıtım, değişen adet) reddeder.
export default {
  components: { BulkDistributePicker },
  props: {
    report: { type: Object, required: true },
  },
  data() {
    return {
      dimensions: DIMENSIONS,
      breakdown: null,
      tab: "agent_id",
      search: "",
      selected: { agent_id: [], retention_id: [], utm_source: [] },
      slots: [
        { field: "agent_id", label: "Agent", enabled: true, all: false, users: [] },
        { field: "retention_id", label: "Retention", enabled: false, all: false, users: [] },
      ],
      options: { agent_id: [], retention_id: [] },
      hideNotes: false,
      historyOpen: false,
      loading: true,
      running: false,
      error: null,
      lastResult: null,
    };
  },
  computed: {
    currentDim() {
      return DIMENSIONS.find((d) => d.key == this.tab);
    },
    activeFilterDims() {
      return DIMENSIONS.filter((d) => this.selected[d.key].length);
    },
    selectedCount() {
      if (!this.breakdown) return 0;
      return this.breakdown.groups
        .filter((g) => this.groupMatches(g, null))
        .reduce((sum, g) => sum + g.count, 0);
    },
    // Aktif sekmenin kırılımı; adetler diğer sekmelerdeki seçimlere göre daralır
    facetRows() {
      if (!this.breakdown) return [];
      const key = this.tab;
      const counts = {};
      this.breakdown.groups.forEach((g) => {
        if (!this.groupMatches(g, key)) return;
        const value = String(g[key]);
        counts[value] = (counts[value] || 0) + g.count;
      });
      // Seçili olup artık adedi kalmayanlar da görünsün ki seçimi kaldırılabilsin
      this.selected[key].forEach((value) => {
        if (!(value in counts)) counts[value] = 0;
      });
      return Object.keys(counts)
        .map((value) => ({ value, label: this.valueLabel(key, value), count: counts[value] }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "tr"));
    },
    visibleFacetRows() {
      const q = this.search.trim().toLocaleLowerCase("tr");
      if (!q) return this.facetRows;
      return this.facetRows.filter((r) => r.label.toLocaleLowerCase("tr").includes(q));
    },
    facetMax() {
      return Math.max(1, ...this.facetRows.map((r) => r.count));
    },
    allVisibleSelected() {
      return (
        this.visibleFacetRows.length > 0 &&
        this.visibleFacetRows.every((r) => this.selected[this.tab].includes(r.value))
      );
    },
  },
  created() {
    Promise.all([this.loadBreakdown(), this.loadOptions()]).finally(() => {
      this.loading = false;
    });
  },
  methods: {
    async loadBreakdown() {
      try {
        this.breakdown = (await http.getImportDoubles(this.report.id)).data;
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || "Kırılım yüklenemedi.";
      }
    },
    // Kişi listesi, liste sayfasındaki toplu atamayla aynı: alanın /getOptionList seçenekleri
    async loadOptions() {
      try {
        const cache = this.$root.cache;
        const [fieldsResp] = await Promise.all([
          http.fields("leads"),
          Object.keys(cache.getOptions || {}).length
            ? null
            : http.getOptionList().then((resp) => (cache.getOptions = resp.data)),
        ]);
        const fields = fieldsResp.data || [];
        this.slots.forEach((slot) => {
          const field = fields.find((f) => f.name == slot.field);
          const options = field && cache.getOptions[field.id];
          this.options[slot.field] = Object.values(options || {})
            .filter((o) => Number(o.value) > 0)
            .map((o) => ({ id: Number(o.value), name: o.label }));
        });
      } catch (e) {
        this.error = "Kişi listesi yüklenemedi.";
      }
    },
    groupMatches(group, exceptKey) {
      return DIMENSIONS.every(
        (d) =>
          d.key == exceptKey ||
          !this.selected[d.key].length ||
          this.selected[d.key].includes(String(group[d.key]))
      );
    },
    valueLabel(key, value) {
      const dim = DIMENSIONS.find((d) => d.key == key);
      if (dim.user) {
        if (Number(value) === 0) return "Atanmamış";
        return this.breakdown.users[value] || this.optionName(key, value) || "#" + value;
      }
      return value === "" ? "(boş)" : value;
    },
    optionName(field, id) {
      const option = (this.options[field] || []).find((o) => o.id == id);
      return option ? option.name : null;
    },
    userName(field, id) {
      return this.optionName(field, id) || (this.breakdown && this.breakdown.users[id]) || "#" + id;
    },
    selectedLabels(key) {
      const labels = this.selected[key].map((v) => this.valueLabel(key, v));
      return labels.length > 2 ? `${labels.slice(0, 2).join(", ")} +${labels.length - 2}` : labels.join(", ");
    },
    setTab(key) {
      this.tab = key;
      this.search = "";
    },
    toggleValue(key, value) {
      const list = this.selected[key];
      this.selected[key] = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
    },
    toggleAllVisible() {
      const values = this.visibleFacetRows.map((r) => r.value);
      const list = this.selected[this.tab];
      this.selected[this.tab] = this.allVisibleSelected
        ? list.filter((v) => !values.includes(v))
        : [...new Set([...list, ...values])];
    },
    clearFilters() {
      DIMENSIONS.forEach((d) => (this.selected[d.key] = []));
    },
    // { utm_source: ["google"], agent_id: [12] } — backend'in beklediği tiplerle
    filtersPayload() {
      const filters = {};
      this.activeFilterDims.forEach((d) => {
        filters[d.key] = this.selected[d.key].map((v) => (d.user ? Number(v) : v));
      });
      return filters;
    },
    describeFilters(filters) {
      const parts = DIMENSIONS.filter((d) => filters && filters[d.key] && filters[d.key].length).map(
        (d) => `${d.label}: ${filters[d.key].map((v) => this.valueLabel(d.key, String(v))).join(", ")}`
      );
      return parts.length ? parts.join(" · ") : "Tüm double kayıtlar";
    },
    describeSummary(summary) {
      return Object.keys(summary || {})
        .map((field) => {
          const dim = DIMENSIONS.find((d) => d.key == field);
          const items = Object.keys(summary[field]).map(
            (userId) => `${this.userName(field, userId)}: ${summary[field][userId]}`
          );
          return `${dim ? dim.label : field} → ${items.join(", ")}`;
        })
        .join(" | ");
    },
    perPerson(n) {
      if (!n) return 0;
      const min = Math.floor(this.selectedCount / n);
      const max = Math.ceil(this.selectedCount / n);
      return min === max ? `${min}` : `${min}–${max}`;
    },
    // { agent_id: [5, 8] }; geçersizse hata mesajı döner
    distribution() {
      const active = this.slots.filter((s) => s.enabled);
      if (!active.length) return "Agent veya Retention atamasından en az birini seçin.";
      const payload = {};
      for (const slot of active) {
        const users = slot.all ? this.options[slot.field].map((o) => o.id) : slot.users;
        if (!users.length) return `${slot.label} için en az bir kişi seçin.`;
        payload[slot.field] = [...users];
      }
      return payload;
    },
    async submit() {
      this.error = null;
      const distribute = this.distribution();
      if (typeof distribute === "string") {
        this.error = distribute;
        return;
      }
      const filters = this.filtersPayload();
      const targets = this.slots
        .filter((s) => s.enabled)
        .map((s) => `${s.label}: ${distribute[s.field].length} kişi`)
        .join(", ");
      if (
        !confirm(
          `${this.selectedCount} kayıt dağıtılacak.\nKırılım: ${this.describeFilters(filters)}\n${targets}\n\nOnaylıyor musunuz?`
        )
      ) {
        return;
      }

      this.running = true;
      try {
        const resp = await http.distributeImportReport(this.report.id, {
          distribute,
          filters,
          expected_count: this.selectedCount,
          last_distribution_id: this.breakdown.last_distribution_id,
          hide_notes: this.slots[0].enabled && this.hideNotes ? 1 : 0,
        });
        this.lastResult = { ...resp.data, filters };
        EventBus.$emit("bottomMessage", {
          open: true,
          type: "success",
          title: "Kayıtlar dağıtıldı",
          duration: 5000,
          body: `${resp.data.lead_count} double kayıt dağıtıldı.`,
        });
        // Dağıtılan kırılım artık başkasında; yeni işleme temiz başlansın
        this.clearFilters();
        this.slots.forEach((s) => (s.users = []));
        await this.loadBreakdown();
        this.$emit("distributed");
      } catch (e) {
        const data = (e.response && e.response.data) || {};
        this.error = data.message || "Dağıtım yapılamadı, hiçbir kayıt değiştirilmedi.";
        // Ekran eskimiş: güncel adetleri ve geçmişi getir, seçimler korunur
        if (data.code === "stale") await this.loadBreakdown();
      } finally {
        this.running = false;
      }
    },
    close() {
      if (!this.running) this.$emit("close");
    },
  },
};
</script>

<style scoped>
.tabs {
  @apply flex border-b border-gray-200 text-sm;
}
.tabs button {
  @apply px-4 py-2 text-gray-600 border-b-2 border-transparent whitespace-nowrap;
  margin-bottom: -1px;
}
.tabs button:hover {
  @apply text-gray-900;
}
.tabs button.active {
  @apply font-medium text-gray-900 border-gray-900;
}
.assign-mode {
  @apply inline-flex rounded-lg ring-1 ring-inset ring-gray-300 overflow-hidden text-sm flex-shrink-0;
  height: 32px;
}
.assign-mode button {
  @apply px-3 text-gray-600 bg-white transition-colors whitespace-nowrap;
}
.assign-mode button + button {
  @apply border-l border-gray-300;
}
.assign-mode button:hover {
  @apply bg-gray-100;
}
.assign-mode button.active {
  @apply bg-gray-900 text-white;
}
</style>
